import type { AuthMe } from '@saas/ui';

/**
 * ログインが 2 段階になる経路（2 要素認証・Google / Microsoft ログイン）のための橋渡し。
 *
 * `useAuth().login()` は「メール + パスワード」を前提にした 1 回きりの口なので、
 *   ① 1 段目で 2 要素が要求された  → `TwoFactorRequiredError` を投げてもらい、画面が 2 段目を出す
 *   ② 2 段目 / OIDC でトークンが出た → `applyIssuedSession()` でシェルのセッションに載せる
 * という形にしてある。実際に載せるのは apps/web の AuthProvider（起動時に registerSessionApplier を呼ぶ）。
 *
 * ★ この仕組みは `@saas/ui` の AuthContextValue を変えずに済ませるためのもの。
 *   トークンをここに溜め込まない（渡してすぐ忘れる）。
 */
export interface IssuedSession {
  accessToken: string;
  refreshToken: string;
  expiresInSec: number;
  me: AuthMe;
}

type SessionApplier = (session: IssuedSession) => void;

let applier: SessionApplier | null = null;

/** apps/web の AuthProvider が起動時に登録する（引数 null で解除） */
export function registerSessionApplier(fn: SessionApplier | null): void {
  applier = fn;
}

export function hasSessionApplier(): boolean {
  return applier !== null;
}

export function applyIssuedSession(session: IssuedSession): void {
  if (!applier) throw new Error('ログイン結果の受け取り先が登録されていません（apps/web の AuthProvider が registerSessionApplier を呼びます）');
  applier(session);
}

/**
 * 1 段目（パスワード / IdP）は通ったが、2 要素認証の確認コードが要る状態。
 * `twoFactorToken` は 5 分で失効する署名つきトークン（それ自体では何もできない）。
 */
export class TwoFactorRequiredError extends Error {
  readonly twoFactorToken: string;
  readonly expiresInSec: number;

  constructor(twoFactorToken: string, expiresInSec: number) {
    super('2 要素認証の確認コードが必要です');
    this.name = 'TwoFactorRequiredError';
    this.twoFactorToken = twoFactorToken;
    this.expiresInSec = expiresInSec;
  }
}

export function isTwoFactorRequired(e: unknown): e is TwoFactorRequiredError {
  return e instanceof TwoFactorRequiredError || (typeof e === 'object' && e !== null && (e as { name?: string }).name === 'TwoFactorRequiredError');
}
